import { Check, Settings, Zap, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from "@/lib/utils"
import type { IntegrationType } from '@/lib/integrations-constants'

interface IntegrationCardProps {
  integration: {
    id: string
    name: string
    description: string
    type: IntegrationType
    icon: React.ReactNode
    color: string
    tags: string[]
    enabled?: boolean
    logoUrl?: string
    externalUrl?: string
  }
  onConfigure: () => void
  onConnect: () => void
  onDisconnect?: () => void
}

export function IntegrationCard({
  integration,
  onConfigure,
  onConnect,
  onDisconnect,
}: IntegrationCardProps) {
  const { t } = useTranslation(['integrations'])
  const isConnected = integration.enabled ?? false

  return (
    <Card className="group relative flex h-full flex-col overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-primary/50">
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

      <div className="relative flex flex-1 flex-col p-5">
        {/* Header: Icon + Name + Status */}
        <div className="mb-4">
          <div className="flex items-start gap-3">
            {/* Icon with colored background */}
            <div
              className={cn(
                'flex h-14 w-14 shrink-0 items-center justify-center rounded-lg transition-transform duration-200 group-hover:scale-105',
                integration.logoUrl ? 'bg-white border border-gray-200 p-2.5 shadow-sm' : integration.color
              )}
            >
              {integration.logoUrl ? (
                <img
                  src={integration.logoUrl}
                  alt={integration.name}
                  className="h-full w-full object-contain"
                />
              ) : (
                integration.icon
              )}
            </div>

            {/* Name and Status */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base leading-tight mb-1.5">
                {integration.name}
              </h3>
              {isConnected ? (
                <Badge
                  variant="default"
                  className="bg-green-500/10 text-green-700 hover:bg-green-500/20 border-green-200"
                >
                  <Check className="h-3 w-3 mr-1" />
                  {t('integrations:card.connected')}
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-muted-foreground">
                  <Zap className="h-3 w-3 mr-1" />
                  {t('integrations:card.available')}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {integration.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4 flex-1">
          {integration.tags.slice(0, 3).map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className="text-xs font-normal h-6"
            >
              {tag}
            </Badge>
          ))}
          {integration.tags.length > 3 && (
            <Badge variant="outline" className="text-xs font-normal h-6">
              +{integration.tags.length - 3}
            </Badge>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-auto pt-3 border-t">
          {isConnected ? (
            <Button
              size="sm"
              className="flex-1"
              onClick={onConfigure}
            >
              <Settings className="h-4 w-4 mr-2" />
              {t('integrations:card.configure')}
            </Button>
          ) : integration.externalUrl ? (
            <Button
              size="sm"
              className="flex-1"
              onClick={() => window.open(integration.externalUrl, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('integrations:card.connectOn', { name: integration.name })}
            </Button>
          ) : (
            <Button
              size="sm"
              className="flex-1"
              onClick={onConnect}
            >
              <Zap className="h-4 w-4 mr-2" />
              {t('integrations:card.connect')}
            </Button>
          )}
          {onDisconnect && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDisconnect}
              className="text-muted-foreground hover:text-destructive"
            >
              {t('integrations:card.disconnect')}
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}
